/**
 * Agents — Layout route
 *
 * Wraps all /agents/* child routes with ContentHeader + tab navigation.
 * Tabs: Hub (table), Gallery (cards)
 */

import { createFileRoute, Outlet, redirect, useLocation, useNavigate } from '@tanstack/react-router';
import { ContentHeader } from '@/components/content-header';
import { Bot, LayoutGrid, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/agents')({
  // fix-wave-4 PR-AG (Bug #1037) — beforeLoad guard. Pre-fix the SPA
  // relied on the __root.tsx useEffect to bounce unauth'd users to
  // /login. useEffect runs AFTER the first render, so a fresh-context
  // browser navigating to `/agents` saw the shell flash on screen + the
  // URL stayed at `/agents` (the API calls 401'd but the page never
  // redirected). Pen-test scripts that wait for networkidle caught this.
  //
  // beforeLoad runs SYNCHRONOUSLY before any component mounts, so the
  // redirect throws away the load entirely. Match the publicRoutes list
  // in __root.tsx (login/auth/invite/setup). All other agents/* child
  // routes inherit this guard via TanStack's route hierarchy.
  beforeLoad: () => {
    if (typeof window === 'undefined') return;
    const token = window.localStorage.getItem('accessToken');
    if (!token) {
      throw redirect({ to: '/login' });
    }
  },
  component: AgentsLayout,
});

function AgentsLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const TAB_PATHS = ['/agents', '/agents/', '/agents/gallery', '/agents/gallery/'];
  const isTabPage = TAB_PATHS.includes(location.pathname);

  return (
    <div className="flex flex-col h-full">
      {isTabPage && (
        <ContentHeader
          title="Agents"
          tabs={[
            { label: 'Hub', path: '/agents', icon: Bot },
            { label: 'Gallery', path: '/agents/gallery', icon: LayoutGrid },
          ]}
          actions={
            <Button size="sm" className="gap-1.5" onClick={() => navigate({ to: '/agents/new' })}>
              <Plus className="h-3.5 w-3.5" /> New Agent
            </Button>
          }
        />
      )}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
